import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LabDemoProvider } from "./context/LabDemoContext";
import { NotificationProvider } from "./context/NotificationContext";
import MainLayout from "./components/Layout/MainLayout";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Pulse from "./pages/Pulse";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Focus from "./pages/Focus";
import Tasks from "./pages/Tasks";
import Match from "./pages/Match";
import Collab from "./pages/Collab";
import Team from "./pages/Team";
import Lanes from "./pages/Lanes";
import Lab from "./pages/Lab";
import Insights from "./pages/Insights";
import Structure from "./pages/Structure";
import Threads from "./pages/Threads";
import Settings from "./pages/Settings";
import Intelligence from "./pages/Intelligence";
import Inbox from "./pages/Inbox";
import OnboardingOverlay from "./components/Onboarding/OnboardingOverlay";

// Protected Route Component with Onboarding Check
const ProtectedRoute = ({ children }) => {
  const { user, loading, authMode } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  
  useEffect(() => {
    if (user && !loading) {
      // Skip onboarding for Supabase authenticated users - go straight to app
      if (authMode === 'supabase') {
        setShowOnboarding(false);
        setOnboardingChecked(true);
        return;
      }
      
      // Check for test bypass parameter (for testing only)
      const urlParams = new URLSearchParams(window.location.search);
      const skipParam = urlParams.get('skipOnboarding');
      const skipFromStorage = localStorage.getItem('skip_onboarding_test');
      
      if (skipParam === 'true' || skipFromStorage === 'true') {
        // Store skip preference for navigation
        localStorage.setItem('skip_onboarding_test', 'true');
        setShowOnboarding(false);
      } else {
        // Show onboarding for demo mode users
        setShowOnboarding(true);
      }
      setOnboardingChecked(true);
    }
  }, [user, loading, authMode]);
  
  if (loading || !onboardingChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F6' }}>
        <p style={{ color: '#8D99AE' }}>Loading...</p>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Show onboarding overlay only for demo mode (not Supabase users)
  if (showOnboarding) {
    return (
      <OnboardingOverlay 
        onComplete={(data) => {
          if (data) {
            localStorage.setItem(`cognitive_profile_${user.id || 'demo'}`, JSON.stringify(data));
          }
          setShowOnboarding(false);
        }}
      />
    );
  }
  
  return children;
};

// Onboarding Preview Route (for testing)
const OnboardingPreview = () => {
  return (
    <OnboardingOverlay 
      onComplete={(data) => {
        console.log('Onboarding completed:', data);
        window.location.href = '/pulse';
      }}
      onSkipToDemo={() => {
        window.location.href = '/pulse';
      }}
    />
  );
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
        <LabDemoProvider>
          <BrowserRouter>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/onboarding" element={<OnboardingPreview />} />
              
              {/* Protected Routes - App Dashboard */}
              <Route path="/pulse" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Pulse />} />
              </Route>
              <Route path="/projects" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Projects />} />
                <Route path=":projectId" element={<ProjectDetail />} />
              </Route>
              <Route path="/focus" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Focus />} />
              </Route>
              <Route path="/tasks" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Tasks />} />
              </Route>
              <Route path="/match" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Match />} />
              </Route>
              <Route path="/collab" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Collab />} />
              </Route>
              <Route path="/team" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Team />} />
              </Route>
              <Route path="/structure" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Structure />} />
              </Route>
              <Route path="/threads" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Threads />} />
              </Route>
              <Route path="/inbox" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Inbox />} />
              </Route>
              <Route path="/lanes" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Lanes />} />
              </Route>
              <Route path="/lab" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Lab />} />
              </Route>
              <Route path="/insights" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Insights />} />
              </Route>
              <Route path="/settings" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Settings />} />
              </Route>
              <Route path="/intelligence" element={<ProtectedRoute><MainLayout /></ProtectedRoute>}>
                <Route index element={<Intelligence />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </LabDemoProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
